import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { PatientRow, Severity } from "../lib/types";
import { useI18n } from "../lib/i18n";

const TIERS: Severity[] = ["urgent", "important", "routine"];

export default function PatientList() {
  const [rows, setRows] = useState<PatientRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    api.patients().then(setRows).catch((e: Error) => setErr(e.message));
  }, []);

  if (err) return <p className="error">Could not load patients: {err}</p>;
  if (!rows) return <p className="muted">{t("loading")}</p>;

  return (
    <div className="stack">
      <div className="row">
        <h1 style={{ margin: 0, fontSize: 20 }}>{t("todaysPatients")}</h1>
        <span className="muted small">{t("sortedBy")}</span>
      </div>
      {/* On phones each row becomes a card; data-label supplies the column name there. */}
      <table className="list stackable">
        <thead>
          <tr>
            <th>{t("colPatient")}</th>
            <th>{t("colAgeSex")}</th>
            <th>{t("colWeight")}</th>
            <th>{t("colZ")}</th>
            <th>{t("colRecs")}</th>
            <th>{t("colSource")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr
              key={p.id}
              className={`clickable band ${p.top_severity ?? "info"}`}
              onClick={() => (window.location.hash = `#/patient/${encodeURIComponent(p.id)}`)}
            >
              <td className="name">
                <a href={`#/patient/${encodeURIComponent(p.id)}`} onClick={(e) => e.stopPropagation()}>
                  {p.name ?? p.id}
                </a>
                {p.display_id && <div className="muted small eid">{p.display_id}</div>}
              </td>
              <td data-label={t("colAgeSex")}>
                <span dir="ltr">{p.age_years.toFixed(1)} y · {p.sex === "male" ? "M" : "F"}</span>
              </td>
              <td data-label={t("colWeight")}>
                <span className={`wclass ${p.weight_class ?? ""}`}>{p.weight_label?.split(" (")[0] ?? "No BMI"}</span>
              </td>
              <td data-label={t("colZ")} style={{ fontVariantNumeric: "tabular-nums" }}>
                <span dir="ltr">{p.z != null ? `${p.z >= 0 ? "+" : ""}${p.z.toFixed(2)}` : "–"}</span>
                {p.reference && <div className="muted small">{p.reference}</div>}
              </td>
              <td data-label={t("colRecs")}>
                <div className="counts">
                  {TIERS.filter((tier) => p.counts[tier] > 0).map((tier) => (
                    <span key={tier} className={`tier ${tier}`}>
                      {p.counts[tier]} {t(tier)}
                    </span>
                  ))}
                </div>
              </td>
              <td data-label={t("colSource")} className="muted small">{p.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
